import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import styled from "@emotion/styled";
import { isEmpty } from "ramda";

import PersonPresenter from "presenters/PersonPresenter";
import FacePresenter from "presenters/FacePresenter";

import ScrollArrows from "./components/ScrollArrows";
import FacesSkeleton from "./components/FacesSkeleton";
import PeopleList from "./components/PeopleList";
import FacesList from "./components/FacesList";
import AddFacesButton from "./components/AddFacesButton";

const FacesContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: 10,
  flexWrap: "nowrap",
  overflowX: "scroll",
  overflowY: "hidden",
  flexDirection: "row",
  alignItems: "center",
  columnGap: 5,
  rowGap: 5,
  wrap: "nowrap",
  width: "100%",
  "&::-webkit-scrollbar": { display: "none" },
  scrollbarWidth: "none",
  [theme.breakpoints.up("tablet")]: {
    width: 400,
    display: "grid",
    justifyContent: "space-around",
    alignContent: "space-between",
    alignItems: "end",
    gridTemplateColumns: "1fr 1fr 1fr",
    height: 280,
    overflowX: "hidden",
    overflowY: "scroll",
  },
}));

const Faces = ({ people, faces, isLoading, onAddFace, onFaceSelected, onScroll }) => {
  const [scrollContainerWidth, setScrollContainerWidth] = useState(100);
  const [faceSelected, setFaceSelected] = useState({});
  const noContent = isEmpty(people) && isEmpty(faces);

  useEffect(() => {
    onFaceSelected(faceSelected);
  }, [faceSelected]);

  if (isLoading) return <FacesSkeleton />;
  if (noContent) return null;

  return (
    <Box sx={{ width: "100%" }} data-testid="faces-component">
      <ScrollArrows scrollContainerWidth={scrollContainerWidth}>
        <FacesContainer
          id="scroll-container"
          onScroll={onScroll}
          ref={(el) => {
            if (!el) return;
            setScrollContainerWidth(el.getBoundingClientRect().width);
          }}
        >
          <AddFacesButton onAddFace={onAddFace} />
          <PeopleList people={people} faceSelected={faceSelected} setFaceSelected={setFaceSelected} />
          <FacesList faces={faces} faceSelected={faceSelected} setFaceSelected={setFaceSelected} />
        </FacesContainer>
      </ScrollArrows>
    </Box>
  );
};

Faces.propTypes = {
  people: PropTypes.arrayOf(PersonPresenter.shape()),
  faces: PropTypes.arrayOf(FacePresenter.shape()),
  isLoading: PropTypes.bool.isRequired,
  onAddFace: PropTypes.func.isRequired,
  onFaceSelected: PropTypes.func.isRequired,
  onScroll: PropTypes.func.isRequired,
};

export default Faces;
